// ✅ AdminTools.js — Chunk 1 of N (Imports + Setup + Core State)

import React, { useEffect, useMemo, useState } from 'react';
import { db, auth, app } from '../firebase';
import {
  getDocs,
  getDoc,
  setDoc,
  collection,
  doc,
  updateDoc,
  deleteDoc,
  writeBatch,
  query,
  where,
  addDoc,
} from 'firebase/firestore';
import {
  sendPasswordResetEmail,
} from 'firebase/auth';
import { Link } from 'react-router-dom';
import { useSchool } from '../hooks/useSchool';
import { getFunctions, httpsCallable } from "firebase/functions";

const NON_AWARD_VALUE_TYPES = new Set(['balance correction', 'store refund']);
const isAwardTicket = (ticket, quantity) => quantity > 0
  && !NON_AWARD_VALUE_TYPES.has(
    String(ticket.valueType || '').trim().toLowerCase()
  );

const AdminTools = () => {
  const schoolId = useSchool();

  const [students, setStudents] = useState([]);
  const [classList, setClassList] = useState([]);               // configured classes
  const [classLabels, setClassLabels] = useState({});           // display names
  const [advisoryChanges, setAdvisoryChanges] = useState({});
  const [newClassName, setNewClassName] = useState('');
  const [expandedClasses, setExpandedClasses] = useState({});
  const [staffAndAdmins, setStaffAndAdmins] = useState([]);
  const [isStaffMenuOpen, setIsStaffMenuOpen] = useState(false);

  const [newUserRole, setNewUserRole] = useState('student');
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserClass, setNewUserClass] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [addUserStatus, setAddUserStatus] = useState('');

  const [archiveYear, setArchiveYear] = useState('');
  const [isArchiving, setIsArchiving] = useState(false);
  const [archiveStatus, setArchiveStatus] = useState('');
  const [pbisValues, setPbisValues] = useState([]);            // ✅ NEW: PBIS value list
  const [newPbisValue, setNewPbisValue] = useState('');        // ✅ NEW: Add new value
  const [pbisEditStates, setPbisEditStates] = useState({});    // ✅ NEW: Track edit states

  const currentYear = new Date().getFullYear();
  const possibleYears = [
    `${currentYear - 1}-${currentYear}`,
    `${currentYear}-${currentYear + 1}`,
  ];

  const classesDocRef = schoolId
    ? doc(db, 'schools', schoolId, 'totals', 'classes')
    : doc(db, 'totals', 'classes');

  const legacyClassesDocRef = doc(db, 'totals', 'advisories');

  const labelsDocRef = schoolId
    ? doc(db, 'schools', schoolId, 'totals', 'classLabels')
    : doc(db, 'totals', 'classLabels');

  const legacyLabelsDocRef = doc(db, 'totals', 'advisoryLabels');

  const pbisValuesRef = schoolId ? collection(db, 'schools', schoolId, 'pbisValues') : null;
  const [previewData, setPreviewData] = useState(null);
  const readClassList = async () => {
    let snap = await getDoc(classesDocRef);
    if (snap.exists()) return snap.data().list || [];
    if (schoolId === 'nlcs') {
      const legacy = await getDoc(legacyClassesDocRef);
      return legacy.exists() ? (legacy.data().list || []) : [];
    }
    return [];
  };

  const writeClassList = async (list) => {
    await setDoc(classesDocRef, { list }, { merge: true });
    if (schoolId === 'nlcs') {
      await setDoc(legacyClassesDocRef, { list }, { merge: true });
    }
    setClassList(list);
  };

  const readClassLabels = async () => {
    let snap = await getDoc(labelsDocRef);
    if (snap.exists()) return snap.data() || {};
    if (schoolId === 'nlcs') {
      const legacy = await getDoc(legacyLabelsDocRef);
      return legacy.exists() ? (legacy.data() || {}) : {};
    }
    return {};
  };

  const writeClassLabels = async (labelsObj) => {
    const updatedLabels = { ...labelsObj };
    const oldToNew = {};
    classList.forEach((original) => {
      const updated = labelsObj[original];
      if (updated && updated !== original) oldToNew[original] = updated;
    });

    if (Object.keys(oldToNew).length > 0) {
      const batch = writeBatch(db);
      students.forEach((s) => {
        const grp = s.class || s.advisory;
        if (grp && oldToNew[grp]) {
          batch.update(doc(db, 'users', s.id), {
            class: oldToNew[grp],
            advisory: oldToNew[grp],
          });
        }
      });
      await batch.commit();
      const newList = classList.map(c => oldToNew[c] || c);
      await writeClassList(newList);
    }

    await setDoc(labelsDocRef, updatedLabels, { merge: true });
    if (schoolId === 'nlcs') {
      await setDoc(legacyLabelsDocRef, updatedLabels, { merge: true });
    }
    setClassLabels(updatedLabels);
  };
  const functions = getFunctions(app, "us-central1");

  async function handleAdminSetPassword(uid) {
    const newPassword = window.prompt("Enter a new password (minimum 6 characters):");
    if (!newPassword) return;
  
    try {
      if (!auth.currentUser) {
        alert("❌ You are not signed in.");
        return;
      }
  
      const setPassword = httpsCallable(functions, "adminSetUserPassword");

await setPassword({
  uid,
  newPassword,
});
  
      alert("✅ Password updated successfully");
    } catch (err) {
      console.error("Password update failed:", err);
      alert(`❌ Failed to update password: ${err.message}`);
    }
  }
  // ---- User Creation ----
  const handleAddUser = async () => {
    if (!newUserName || !newUserEmail || !newUserPassword) {
      alert("Please fill out all fields.");
      return;
    }
  
    if (!schoolId) {
      setAddUserStatus("❌ No school is selected.");
      return;
    }

    try {
      const createUser = httpsCallable(functions, "createManagedUser");
      await createUser({
        name: newUserName.trim(),
        email: newUserEmail.trim().toLowerCase(),
        password: newUserPassword,
        role: newUserRole,
        schoolId,
        className: newUserClass?.trim() || 'Unassigned',
      });

      setAddUserStatus("✅ User created successfully!");
      setNewUserName('');
      setNewUserEmail('');
      setNewUserPassword('');
      setNewUserRole('student');
      setNewUserClass('');
    } catch (err) {
      console.error('❌ Failed to add user:', err);
      const message = err?.message || 'Unable to create the user.';
      const isUnavailable = err?.code === 'functions/unavailable'
        || err?.code === 'functions/not-found';
      setAddUserStatus(
        isUnavailable
          ? '❌ User creation is temporarily unavailable. Contact the system administrator to deploy the user-creation service.'
          : `❌ ${message}`
      );
    }
  };
  const handlePreviewArchive = async () => {
    if (!schoolId) return;
  
    try {
      const ticketsRef = collection(db, 'schools', schoolId, 'tickets');
      const usersRef = collection(db, 'users');
  
      const [ticketSnap, usersSnap] = await Promise.all([
        getDocs(ticketsRef),
        getDocs(query(usersRef, where('schoolId', '==', schoolId)))
      ]);
  
      // 🎫 Match TicketSummary logic exactly
      let totalTickets = 0;
      const balancesMap = {};
  
      ticketSnap.forEach(doc => {
        const ticket = doc.data();
  
        const qty = ticket.quantity ?? 1;
        const studentId = ticket.studentId;
  
        // Count actual awards, but not refunds or balance corrections.
        if (isAwardTicket(ticket, qty)) {
          totalTickets += qty;
        }
  
        // ✅ Build balances per student
        if (studentId) {
          if (!balancesMap[studentId]) balancesMap[studentId] = 0;
          balancesMap[studentId] += qty;
        }
      });
  
      const balanceCount = Object.keys(balancesMap).length;
  
      // ➕ Include yearly override (THIS was missing before)
      const totalsRef = doc(db, 'schools', schoolId, 'totals', 'yearly');
      const totalsSnap = await getDoc(totalsRef);
  
      const yearlyOverride = totalsSnap.exists()
        ? totalsSnap.data().totalTicketsOverride || 0
        : 0;
  
      // 📊 Final preview data
      setPreviewData({
        tickets: totalTickets + yearlyOverride,
        balances: balanceCount,
        users: usersSnap.size
      });
  
    } catch (err) {
      console.error(err);
      alert("❌ Failed to load preview.");
    }
  };
const handleResetYear = async () => {
  if (!schoolId || !archiveYear) {
    alert("Please select an archive year first.");
    return;
  }
  const confirmArchive = window.confirm(
    `⚠️ You are about to archive the ${archiveYear} school year.\n\n` +
    `This will:\n` +
    `• Save all tickets, calculated balances, and users\n` +
    `• DELETE all current tickets and balances\n\n` +
    `The current data will only be cleared after the archive is saved.\n\n` +
    `Do you want to continue?`
  );
  
  if (!confirmArchive) return;

  setIsArchiving(true);
  setArchiveStatus('Archiving data securely. Keep this page open…');
  try {
    const archiveSchoolYear = httpsCallable(functions, 'archiveSchoolYear', {
      timeout: 540000,
    });
    const result = await archiveSchoolYear({schoolId, archiveYear});
    const counts = result.data?.counts || {};
    const message = result.data?.alreadyArchived
      ? `The ${archiveYear} archive was already complete.`
      : `Archived ${counts.tickets || 0} tickets and ${counts.users || 0} users.`;
    setArchiveStatus(`✅ ${message} The new school year is ready.`);
    setPreviewData(null);
    alert(`✅ ${message}\n\nYear reset complete!`);
  } catch (err) {
    console.error(err);
    const message = err?.message || "Failed to archive and reset the year.";
    setArchiveStatus(`❌ ${message}`);
    alert(`❌ ${message}`);
  } finally {
    setIsArchiving(false);
  }
};
// ---- Move Students / Update Users ----
const handleMoveStudent = async (studentId, newClass) => {
  try {
    await updateDoc(doc(db, 'users', studentId), {
      class: newClass,
      advisory: newClass,
    });
    setStudents((prev) =>
      prev.map((s) =>
        s.id === studentId ? { ...s, class: newClass, advisory: newClass } : s
      )
    );
    alert(`✅ Moved student to ${newClass}`);
  } catch (err) {
    console.error('Failed to move student:', err);
    alert('❌ Failed to move student');
  }
};

const handleSendPasswordReset = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    alert(`✅ Password reset email sent to ${email}`);
  } catch (err) {
    console.error('Failed to send reset email:', err);
    alert('❌ Failed to send reset email');
  }
};

const handleUpdateUserEmail = async (userId, newEmail) => {
  try {
    await updateDoc(doc(db, 'users', userId), { email: newEmail });
    setStudents((prev) =>
      prev.map((s) => (s.id === userId ? { ...s, email: newEmail } : s))
    );
    setStaffAndAdmins((prev) =>
      prev.map((s) => (s.id === userId ? { ...s, email: newEmail } : s))
    );
    alert(`✅ Email updated to ${newEmail}`);
  } catch (err) {
    console.error('Failed to update email:', err);
    alert('❌ Failed to update email');
  }
};
const deleteStudent = async (studentId) => {
  if (!window.confirm('Are you sure you want to remove this student?')) return;
  try {
    await deleteDoc(doc(db, 'users', studentId));
    setStudents((prev) => prev.filter((s) => s.id !== studentId));
    alert('✅ Student removed.');
  } catch (err) {
    console.error('Failed to remove student:', err);
    alert('❌ Failed to remove student.');
  }
};
const deleteStaffOrAdmin = async (userId) => {
  if (!window.confirm('Are you sure you want to remove this user?')) return;
  try {
    await deleteDoc(doc(db, 'users', userId));
    setStaffAndAdmins((prev) => prev.filter((s) => s.id !== userId));
    alert('✅ Staff/Admin removed.');
  } catch (err) {
    console.error('Failed to remove user:', err);
    alert('❌ Failed to remove user.');
  }
};
// ---- Sync Student Balances from Tickets ----
const handleSyncBalancesFromTickets = async () => {
  if (!schoolId) return;

  const ticketRef = collection(db, 'schools', schoolId, 'tickets');
  const balanceRef = collection(db, 'schools', schoolId, 'balances');

  try {
    const snap = await getDocs(ticketRef);
    const balances = {};

    snap.docs.forEach(doc => {
      const t = doc.data();
      if (!t.studentId) return;
      balances[t.studentId] = (balances[t.studentId] || 0) + (t.quantity ?? 1);
    });

    const batch = writeBatch(db);
    Object.entries(balances).forEach(([studentId, total]) => {
      batch.set(doc(balanceRef, studentId), { balance: total });
    });

    await batch.commit();
    alert("✅ Balances synced successfully!");
  } catch (err) {
    console.error(err);
    alert("❌ Failed to sync balances.");
  }
};

  const fetchPBISValues = async () => {
    if (!pbisValuesRef) return;
    try {
      const snap = await getDocs(pbisValuesRef);
      const values = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setPbisValues(values);
    } catch (err) {
      console.error('Failed to fetch PBIS values:', err);
    }
  };

  useEffect(() => {
    fetchPBISValues();
  }, [schoolId]);
    // ---- load students + staff/admins + class config ----
    useEffect(() => {
      const fetchPeopleAndConfig = async () => {
        if (!schoolId) return;
  
        // Fetch users
        const usersCol = collection(db, 'users');
        const qStudents = query(usersCol, where('schoolId', '==', schoolId));
        const usersSnap = await getDocs(qStudents);
        const allUsers = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
  
        setStudents(allUsers.filter(u => u.role === 'student'));
        setStaffAndAdmins(allUsers.filter(u => u.role === 'staff' || u.role === 'admin'));
  
        // Fetch classes + labels
        const list = await readClassList();
        setClassList(list);
  
        const labels = await readClassLabels();
        setClassLabels(labels);
  
        const collapsedState = {};
        list.forEach(cls => (collapsedState[cls] = false));
        setExpandedClasses(collapsedState);
      };
  
      fetchPeopleAndConfig();
    }, [schoolId]); // eslint-disable-line react-hooks/exhaustive-deps
  
  
    const handleSavePbisValue = async (id, name) => {
      try {
        await updateDoc(doc(pbisValuesRef, id), { name });
        setPbisEditStates(prev => ({ ...prev, [id]: false }));
      } catch (err) {
        console.error(err);
        alert('❌ Failed to save PBIS value.');
      }
    };
      // ---- Class Rename Handlers ----
  const handleLabelChange = (cls, newLabel) => {
    setClassLabels(prev => ({ ...prev, [cls]: newLabel }));
  };

  const handleSaveLabelChanges = async () => {
    await writeClassLabels(classLabels);
    alert('✅ Class labels updated.');
  };
  const handleResetStudentPassword = async (email) => {
    if (!email) return;
    try {
      await sendPasswordResetEmail(auth, email);
      alert(`✅ Password reset email sent to ${email}`);
    } catch (err) {
      console.error(err);
      alert(`❌ Failed to send reset email: ${err.message}`);
    }
  };

  // ---- Class Add/Remove/Sync ----
  const handleAddClass = async () => {
    const name = newClassName.trim();
    if (!name || classList.includes(name)) return;
    const updated = [...classList, name];
    await writeClassList(updated);
    setNewClassName('');
  };

  const handleRemoveClass = async (cls) => {
    if (!window.confirm(`Remove class "${cls}" from the list?`)) return;
    const updated = classList.filter(c => c !== cls);
    await writeClassList(updated);
  };

  const handleSyncClassesFromStudents = async () => {
    const studentClasses = new Set(
      students.map(s => s.class || s.advisory).filter(Boolean)
    );
    const updatedList = Array.from(studentClasses);
    await writeClassList(updatedList);
    alert('✅ Synced classes from students.');
  };
    // ---- PBIS Value Handlers ----
    const handleAddPbisValue = async () => {
      const trimmed = newPbisValue.trim();
      if (!trimmed || !pbisValuesRef) return;
      try {
        const docRef = await addDoc(pbisValuesRef, { name: trimmed });
        setPbisValues(prev => [...prev, { id: docRef.id, name: trimmed }]);
        setNewPbisValue('');
      } catch (err) {
        console.error('❌ Failed to add PBIS value:', err);
        alert('❌ Could not add value.');
      }
    };
  
    const handleDeletePbisValue = async (id) => {
      if (!pbisValuesRef) return;
      if (!window.confirm('Delete this value?')) return;
      try {
        await deleteDoc(doc(pbisValuesRef, id));
        setPbisValues(prev => prev.filter(v => v.id !== id));
      } catch (err) {
        console.error('❌ Failed to delete PBIS value:', err);
      }
    };
  
    const handleEditPbisValue = async (id, newName) => {
      if (!pbisValuesRef || !newName.trim()) return;
      try {
        await updateDoc(doc(pbisValuesRef, id), { name: newName.trim() });
        setPbisValues(prev =>
          prev.map(v => (v.id === id ? { ...v, name: newName.trim() } : v))
        );
        setPbisEditStates(prev => ({ ...prev, [id]: false }));
      } catch (err) {
        console.error('❌ Failed to update PBIS value:', err);
      }
    };
  
    const toggleEditMode = (id, currentName) => {
      setPbisEditStates(prev => ({
        ...prev,
        [id]: prev[id] === false ? currentName : false,
      }));
    };
    return (
      <div className="dashboard-container">
        {/* ✅ PBIS Values Manager */}
  <div className="section-card">
  <h3>🌟 PBIS Values</h3>
  <p>Manage the core values your school rewards students for. These appear when writing tickets and in the ticket summary.</p>

  {/* ➕ Add New PBIS Value */}
  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
    <input
      type="text"
      placeholder="New value (e.g., Respect)"
      value={newPbisValue}
      onChange={(e) => setNewPbisValue(e.target.value)}
      style={{ flex: '1', padding: '0.5rem' }}
    />
    <button
      onClick={handleAddPbisValue}
      style={{
        backgroundColor: '#22c55e',
        color: 'white',
        padding: '0.5rem 1rem',
        borderRadius: '6px',
      }}
    >
      ➕ Add
    </button>
  </div>

  {/* 📝 Existing PBIS Values */}
  {pbisValues.length === 0 ? (
    <p>No values yet.</p>
  ) : (
    pbisValues.map((val) => (
      <div
        key={val.id}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '0.5rem',
        }}
      >
        {pbisEditStates[val.id] ? (
          <>
            <input
              type="text"
              value={pbisEditStates[val.id]}
              onChange={(e) =>
                setPbisEditStates(prev => ({
                  ...prev,
                  [val.id]: e.target.value,
                }))
              }
              style={{ flex: '1', padding: '0.25rem' }}
            />
            <button
              onClick={() => handleEditPbisValue(val.id, pbisEditStates[val.id])}
              style={{
                backgroundColor: '#2563eb',
                color: 'white',
                padding: '0.25rem 0.5rem',
                borderRadius: '4px',
              }}
            >
              Save
            </button>
            <button
              onClick={() =>
                setPbisEditStates(prev => ({ ...prev, [val.id]: false }))
              }
              style={{
                backgroundColor: '#9ca3af',
                color: 'white',
                padding: '0.25rem 0.5rem',
                borderRadius: '4px',
              }}
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <span style={{ flex: '1' }}><strong>{val.name}</strong></span>
            <button
              onClick={() => toggleEditMode(val.id, val.name)}
              style={{
                backgroundColor: '#3b82f6',
                color: 'white',
                padding: '0.25rem 0.5rem',
                borderRadius: '4px',
              }}
            >
              Edit
            </button>
            <button
              onClick={() => handleDeletePbisValue(val.id)}
              style={{
                backgroundColor: '#ef4444',
                color: 'white',
                padding: '0.25rem 0.5rem',
                borderRadius: '4px',
              }}
            >
              Delete
            </button>
          </>
        )}
      </div>
    ))
  )}
</div>
  {/* ✏️ Manage Class Labels */}
  <div className="section-card">
    <h3>✏️ Manage Class Labels</h3>
    <p>Customize how classes are displayed throughout the app. These changes will also update student records if names are changed.</p>

    {classList.map(cls => (
      <div key={cls} style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center' }}>
        <strong style={{ minWidth: '100px' }}>{cls}</strong>
        <input
          type="text"
          value={classLabels[cls] || ''}
          onChange={(e) => setClassLabels(prev => ({ ...prev, [cls]: e.target.value }))}
          placeholder={cls}
          style={{ flex: '1', padding: '0.25rem', marginLeft: '1rem' }}
        />
      </div>
    ))}

    <button
      onClick={() => writeClassLabels(classLabels)}
      style={{
        marginTop: '1rem',
        backgroundColor: '#22c55e',
        color: 'white',
        padding: '0.5rem 1rem',
        borderRadius: '6px',
      }}
    >
      💾 Save Label Changes
    </button>
  </div>
    {/* ➕ Add / Remove Classes */}
    <div className="section-card">
    <h3>➕ Add / Remove Classes</h3>
    <input
      type="text"
      placeholder="New class name"
      value={newClassName}
      onChange={(e) => setNewClassName(e.target.value)}
      style={{ marginRight: '1rem', padding: '0.25rem', borderRadius: '4px' }}
    />
    <button
      onClick={handleAddClass}
      style={{
        backgroundColor: '#22c55e',
        color: 'white',
        padding: '0.25rem 0.5rem',
        borderRadius: '4px',
      }}
    >
      Add Class
    </button>
    <button
      onClick={handleSyncClassesFromStudents}
      style={{
        marginLeft: '1rem',
        backgroundColor: '#3b82f6',
        color: 'white',
        padding: '0.25rem 0.5rem',
        borderRadius: '4px',
      }}
    >
      Sync From Students
    </button>

    <div style={{ marginTop: '1rem' }}>
      {classList.map(cls => (
        <button
          key={cls}
          onClick={() => handleRemoveClass(cls)}
          style={{
            marginRight: '0.5rem',
            backgroundColor: '#ef4444',
            color: 'white',
            padding: '0.25rem 0.5rem',
            borderRadius: '4px',
          }}
        >
          Remove {cls}
        </button>
      ))}
    </div>
  </div>
 {/* 🔄 Manage Students */}
<div className="section-card">
  <h3 className="section-title">🔄 Manage Students</h3>

  {classList.map((cls) => {
    const studentsInClass = students.filter(
      (s) => (s.class || s.advisory) === cls
    );

    return (
      <div key={cls} style={{ marginBottom: '1rem' }}>
        <button
          onClick={() =>
            setExpandedClasses((prev) => ({
              ...prev,
              [cls]: !prev[cls],
            }))
          }
          style={{
            backgroundColor: '#f3f4f6',
            color: '#1f2937',
            padding: '0.4rem 0.75rem',
            fontWeight: 'bold',
            borderRadius: '6px',
            border: '1px solid #d1d5db',
            cursor: 'pointer',
            width: '100%',
            textAlign: 'left',
          }}
        >
          {expandedClasses[cls] ? '▼' : '▶'} {cls} ({studentsInClass.length})
        </button>

        {expandedClasses[cls] && studentsInClass.length === 0 && (
          <p style={{ marginLeft: '1rem', fontStyle: 'italic', color: '#6b7280' }}>
            No students in this class.
          </p>
        )}

        {expandedClasses[cls] &&
          studentsInClass.map((s, index) => (
            <div
              key={s.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0.75rem',
                backgroundColor: index % 2 === 0 ? '#f9fafb' : '#f3f4f6',
                borderRadius: '6px',
                marginTop: '0.5rem',
                gap: '1rem',
              }}
            >
              {/* 🧑 Name + Email Input */}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 'bold' }}>{s.name}</div>
                <div style={{ fontSize: '0.9rem', color: '#4b5563' }}>
                  <strong>Email:</strong>{' '}
                  <input
                    type="email"
                    value={s.email || ''}
                    onChange={(e) => {
                      const newEmail = e.target.value;
                      setStudents((prev) =>
                        prev.map((stu) =>
                          stu.id === s.id ? { ...stu, email: newEmail } : stu
                        )
                      );
                    }}
                    style={{
                      padding: '0.3rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      width: '220px',
                    }}
                  />
                  <button
                    onClick={() => handleUpdateUserEmail(s.id, s.email)}
                    style={{
                      marginLeft: '0.5rem',
                      backgroundColor: '#10b981',
                      color: 'white',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      border: 'none',
                    }}
                  >
                    Save
                  </button>
                </div>
              </div>

              {/* 🔀 Move to Dropdown */}
              <div style={{ flexShrink: 0 }}>
                <select
                  value={advisoryChanges[s.id] || ''}
                  onChange={(e) =>
                    setAdvisoryChanges((prev) => ({
                      ...prev,
                      [s.id]: e.target.value,
                    }))
                  }
                  style={{
                    padding: '0.3rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    marginBottom: '0.25rem',
                    minWidth: '140px',
                  }}
                >
                  <option value="">-- Move to --</option>
                  <option value="Unassigned">Unassigned</option>
                  {classList.map((clsOption) => (
                    <option key={clsOption} value={clsOption}>
                      {clsOption}
                    </option>
                  ))}
                </select>
                <br />
                <button
                  onClick={() => handleMoveStudent(s.id, advisoryChanges[s.id])}
                  style={{
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    padding: '0.3rem 0.6rem',
                    borderRadius: '4px',
                    border: 'none',
                    width: '100%',
                  }}
                  disabled={!advisoryChanges[s.id]}
                >
                  Move
                </button>
              </div>

              {/* 🔧 Action Buttons */}
<div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
  {/* ✅ Admin-set password (no email needed) */}
  <button
    onClick={() => handleAdminSetPassword(s.uid || s.id)}
    style={{
      backgroundColor: "#22c55e",
      color: "white",
      padding: "0.3rem 0.6rem",
      borderRadius: "4px",
      border: "none",
    }}
    title="Set a new password directly (no email)"
  >
    Set PW
  </button>

  {/* 📧 Email reset (optional) */}
  <button
    onClick={() => handleResetStudentPassword(s.email)}
    style={{
      backgroundColor: "#3b82f6",
      color: "white",
      padding: "0.3rem 0.6rem",
      borderRadius: "4px",
      border: "none",
    }}
    disabled={!s.email}
    title={s.email ? "Send password reset email" : "Student has no email"}
  >
    Email Reset
  </button>

  <button
    onClick={() => deleteStudent(s.id)}
    style={{
      backgroundColor: "#ef4444",
      color: "white",
      padding: "0.3rem 0.6rem",
      borderRadius: "4px",
      border: "none",
    }}
  >
    Remove
  </button>
</div>
            </div>
          ))}
      </div>
    );
  })}
</div>
    {/* 👔 Manage Staff & Admins */}
<div className="section-card">
  <h3>👔 Manage Staff & Admins</h3>
  <button
    type="button"
    onClick={() => setIsStaffMenuOpen((isOpen) => !isOpen)}
    aria-expanded={isStaffMenuOpen}
    aria-controls="staff-admin-list"
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      background: 'none',
      border: 'none',
      padding: 0,
      cursor: 'pointer',
      font: 'inherit',
      textAlign: 'left',
    }}
  >
    <span>
      {isStaffMenuOpen ? 'Hide' : 'Show'} Staff & Admins ({staffAndAdmins.length})
    </span>
    <span aria-hidden="true" style={{ fontSize: '1.1rem' }}>
      {isStaffMenuOpen ? '▴' : '▾'}
    </span>
  </button>
  {isStaffMenuOpen && (
  <div id="staff-admin-list" style={{ marginTop: '1rem' }}>
    {staffAndAdmins.length === 0 ? (
    <p>No staff or admins found.</p>
  ) : (
    staffAndAdmins.map((user) => (
      <div
        key={user.id}
        style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '0.5rem',
          backgroundColor: '#f5f5f5',
          padding: '0.5rem',
          borderRadius: '6px',
        }}
      >
        <div style={{ flex: '1 1 200px' }}>
          <strong>{user.name}</strong> ({user.role})
        </div>
        <div style={{ flex: '1 1 300px' }}>{user.email}</div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {user.email && (
            <>
              <button
                onClick={() => handleSendPasswordReset(user.email)}
                style={{
                  backgroundColor: '#2563eb',
                  color: 'white',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Reset Password
              </button>
              <button
                onClick={() => deleteStaffOrAdmin(user.id)}
                style={{
                  backgroundColor: '#ef4444',
                  color: 'white',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Remove
              </button>
            </>
          )}
        </div>
      </div>
    ))
  )}
  </div>
  )}
</div>
    {/* ➕ Add New User (Student / Staff / Admin) */}
    <div className="section-card">
    <h3>➕ Add New User (Student / Staff / Admin)</h3>

    <select
      value={newUserRole}
      onChange={(e) => setNewUserRole(e.target.value)}
      className="border p-2 mb-2 w-full"
    >
      <option value="student">Student</option>
      <option value="staff">Staff</option>
      <option value="admin">Admin</option>
    </select>

    <input
      type="text"
      placeholder="Name"
      value={newUserName}
      onChange={(e) => setNewUserName(e.target.value)}
      className="border p-2 mb-2 w-full"
    />
    <input
      type="email"
      placeholder="Email"
      value={newUserEmail}
      onChange={(e) => setNewUserEmail(e.target.value)}
      className="border p-2 mb-2 w-full"
    />
    <input
      type="password"
      placeholder="Password"
      value={newUserPassword}
      onChange={(e) => setNewUserPassword(e.target.value)}
      className="border p-2 mb-2 w-full"
    />
    {newUserRole === 'student' && (
  <select
    value={newUserClass}
    onChange={(e) => setNewUserClass(e.target.value)}
    className="border p-2 mb-2 w-full"
  >
    <option value="">-- Optional: Assign to Class --</option>
    <option value="Unassigned">Unassigned</option>
    {classList.map((cls) => (
      <option key={cls} value={cls}>
        {classLabels[cls] || cls}
      </option>
    ))}
  </select>
)}

    <button
      onClick={handleAddUser}
      className="bg-green-600 text-white px-4 py-2 rounded"
    >
      Add User
    </button>

    {addUserStatus && <p className="mt-2">{addUserStatus}</p>}
  </div>
    {/* 🧹 Reset Tools */}
    <div className="section-card">
    <h3>🧹 Reset Tools</h3>

    <select
      value={archiveYear}
      onChange={(e) => setArchiveYear(e.target.value)}
      style={{ marginRight: '1rem', padding: '0.25rem', borderRadius: '4px' }}
    >
      <option value="">-- Select Archive Year --</option>
      {possibleYears.map((year) => (
        <option key={year} value={year}>{year}</option>
      ))}
    </select>
    <button
  onClick={handlePreviewArchive}
  style={{
    backgroundColor: '#6b7280',
    color: 'white',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    marginRight: '1rem',
  }}
>
  👀 Preview Archive
</button>
    <button
      onClick={handleResetYear}
      disabled={isArchiving}
      style={{
        backgroundColor: isArchiving ? '#9ca3af' : '#ef4444',
        color: 'white',
        padding: '0.5rem 1rem',
        borderRadius: '6px',
        marginRight: '1rem',
        cursor: isArchiving ? 'not-allowed' : 'pointer',
      }}
    >
      {isArchiving ? 'Archiving…' : 'Reset Year (Archive + Clear)'}
    </button>

    <button
      onClick={handleSyncBalancesFromTickets}
      style={{
        backgroundColor: '#3b82f6',
        color: 'white',
        padding: '0.5rem 1rem',
        borderRadius: '6px',
      }}
    >
      Sync Balances from Tickets
    </button>
    {previewData && (
  <div style={{ marginTop: '1rem', background: '#f9fafb', padding: '1rem', borderRadius: '6px' }}>
    <strong>Archive Preview:</strong>
    <p>🎫 Tickets: {previewData.tickets}</p>
    <p>💰 Balances: {previewData.balances}</p>
    <p>👥 Users: {previewData.users}</p>
  </div>
)}
    {archiveStatus && (
      <p style={{ marginTop: '1rem', fontWeight: 600 }}>{archiveStatus}</p>
    )}
  </div>
    {/* 🔗 Navigation Footer */}
    <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem' }}>
    <Link to={schoolId ? `/${schoolId}/archives` : '/archives'}>
      <button
        style={{
          backgroundColor: '#2563eb',
          color: 'white',
          padding: '0.5rem 1rem',
          borderRadius: '6px',
        }}
      >
        🗃️ View Archived Data
      </button>
    </Link>

    <Link to={schoolId ? `/${schoolId}/admin` : '/admin'}>
          <button
            style={{
              backgroundColor: '#2563eb',
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
            }}
          >
            ⬅ Back to Admin Dashboard
        </button>
      </Link>
    </div>
  </div> // ✅ closes dashboard-container
); // ✅ closes return
}; // ✅ closes AdminTools component

export default AdminTools;
